// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/OrgLabelEditor.test.tsx
import {
  ASK_PUNTT_ORG_LABEL_MAX_LEN,
  ASK_PUNTT_ORG_TAGS_MAX,
  normalizeOrgLabel,
} from "@mg/schemas/src/christo/askPunttOrgLabels";
import { X } from "@phosphor-icons/react";
import { Badge, Flex, Popover, Text, TextField } from "@radix-ui/themes";
import { type ReactNode, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { type OrgLabel } from "../../../services/askPunttOrgFilters";
import {
  useOrgLabels,
  useUpdateFileLabels,
} from "../../../utils/queries/askPunttOrgFilters";

export type OrgLabelEditorProps = {
  /** Prefix for all testids, e.g. "campaigns" | "chats". */
  testIdPrefix: string;
  /** Entity ID for scoped testids on row-level chips. */
  entityId: string;
  /** Currently assigned tag slugs. */
  value: string[];
  /** Called when tags change. */
  onChange: (slugs: string[]) => void;
  /** Whether the editor is read-only. */
  readOnly?: boolean;
  /**
   * Keep chips on one row (review headers) instead of wrapping under the
   * filename.
   */
  inline?: boolean;
  /**
   * File-row layout: `leading` + add control on one line, chips wrap below
   * so long tags cannot squash the asset card.
   */
  chipsBelow?: boolean;
  /** File/asset control that shares a row with the add button when chipsBelow. */
  leading?: ReactNode;
  /** Optional override for the root testid. */
  "data-testid"?: string;
};

/**
 * Chip input for assigning org tags to a project/chat/file.
 * Shows current tags as removable badges, with a single popover "+" to add
 * more from the org-wide label list or freeform Enter.
 */
export function OrgLabelEditor({
  testIdPrefix,
  entityId,
  value,
  onChange,
  readOnly = false,
  inline = false,
  chipsBelow = false,
  leading,
  "data-testid": dataTestId,
}: OrgLabelEditorProps) {
  const { t } = useTranslation("ui");
  const { data: orgLabels } = useOrgLabels();
  const [filterText, setFilterText] = useState("");
  const [open, setOpen] = useState(false);

  const allLabels: OrgLabel[] = useMemo(
    () => orgLabels?.tags ?? [],
    [orgLabels],
  );

  const displayLabels = useMemo(
    () =>
      value.map((slug) => {
        const found = allLabels.find(
          (l) => l.slug.toLowerCase() === slug.toLowerCase(),
        );
        return found ?? { _id: slug, slug, name: slug };
      }),
    [value, allLabels],
  );

  const availableLabels = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    const selected = new Set(value.map((v) => v.toLowerCase()));
    return allLabels
      .filter((l) => !selected.has(l.slug.toLowerCase()))
      .filter((l) => (q ? l.name.toLowerCase().includes(q) : true));
  }, [allLabels, value, filterText]);

  const handleRemove = useCallback(
    (slug: string) => {
      onChange(value.filter((v) => v.toLowerCase() !== slug.toLowerCase()));
    },
    [value, onChange],
  );

  const handleAdd = useCallback(
    (raw: string) => {
      const normalized = normalizeOrgLabel(raw);
      if (normalized == null) return;
      if (value.some((v) => v.toLowerCase() === normalized.toLowerCase())) {
        setFilterText("");
        return;
      }
      if (value.length >= ASK_PUNTT_ORG_TAGS_MAX) return;
      onChange([...value, normalized]);
      setFilterText("");
    },
    [value, onChange],
  );

  const draft = normalizeOrgLabel(filterText);
  const canCreate =
    draft != null &&
    !value.some((v) => v.toLowerCase() === draft.toLowerCase()) &&
    value.length < ASK_PUNTT_ORG_TAGS_MAX &&
    !availableLabels.some((l) => l.slug.toLowerCase() === draft.toLowerCase());

  const chips = displayLabels.map((label) => (
    <Badge
      key={label._id}
      color="orange"
      variant="soft"
      size="1"
      data-testid={`${testIdPrefix}-row-tag-${entityId}-${label.slug}`}
      className="flex items-center gap-1"
    >
      {label.name}
      {!readOnly && (
        <button
          type="button"
          aria-label={`Remove ${label.name}`}
          data-testid={`${testIdPrefix}-row-tag-remove-${entityId}-${label.slug}`}
          className="ml-0.5 cursor-pointer rounded-full p-0.5 transition-colors hover:bg-puntt-neutral-gray-4"
          onClick={(e) => {
            e.stopPropagation();
            handleRemove(label.slug);
          }}
        >
          <X size={10} aria-hidden="true" />
        </button>
      )}
    </Badge>
  ));

  const addControl = !readOnly ? (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger>
        <button
          type="button"
          data-testid={`${testIdPrefix}-tag-input-${entityId}`}
          aria-label="Add tag"
          className="shrink-0 cursor-pointer rounded border border-dashed border-puntt-neutral-gray-6 px-2 py-0.5 text-xs text-puntt-neutral-gray-9 transition-colors hover:border-puntt-accent-7 hover:text-puntt-accent-11"
        >
          +
        </button>
      </Popover.Trigger>
      <Popover.Content
        align="start"
        aria-label="Add tag"
        data-testid={`${testIdPrefix}-tag-popover-${entityId}`}
        className="max-h-[min(70vh,320px)] min-w-[200px] overflow-y-auto p-2"
      >
        <TextField.Root
          data-testid={`${testIdPrefix}-tag-search-${entityId}`}
          size="1"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (draft) handleAdd(draft);
            }
          }}
          placeholder={t("searchLabels", { defaultValue: "Search or add" })}
          aria-label={t("searchLabels", { defaultValue: "Search or add" })}
          className="mb-2"
          autoFocus
          maxLength={ASK_PUNTT_ORG_LABEL_MAX_LEN}
        />
        {canCreate ? (
          <button
            type="button"
            data-testid={`${testIdPrefix}-label-create-${entityId}`}
            className="mb-1 w-full cursor-pointer rounded px-2 py-1.5 text-left text-sm text-puntt-accent-11 transition-colors hover:bg-puntt-neutral-gray-3"
            onClick={() => handleAdd(draft!)}
          >
            {t("createLabel", {
              defaultValue: `Create “${draft}”`,
              name: draft,
            })}
          </button>
        ) : null}
        {availableLabels.length === 0 && !canCreate ? (
          <Text size="1" className="px-2 py-1 text-puntt-neutral-gray-9">
            {t("noLabelsAvailable", {
              defaultValue: "No labels available",
            })}
          </Text>
        ) : (
          <div className="flex flex-col gap-0.5">
            {availableLabels.map((label) => (
              <button
                key={label._id}
                type="button"
                data-testid={`${testIdPrefix}-label-option-${label.slug}`}
                className="cursor-pointer rounded px-2 py-1.5 text-left text-sm text-puntt-neutral-gray-12 transition-colors hover:bg-puntt-neutral-gray-3"
                onClick={() => handleAdd(label.slug)}
              >
                {label.name}
              </button>
            ))}
          </div>
        )}
      </Popover.Content>
    </Popover.Root>
  ) : null;

  if (chipsBelow) {
    return (
      <div
        data-testid={dataTestId ?? `${testIdPrefix}-label-editor`}
        aria-label="Tags"
        className="flex min-w-0 flex-col gap-1"
      >
        <div
          className="flex min-w-0 items-center gap-2"
          data-testid={`${testIdPrefix}-tag-add-row-${entityId}`}
        >
          {leading}
          {addControl}
        </div>
        {displayLabels.length > 0 ? (
          <Flex
            align="center"
            gap="1"
            wrap="wrap"
            data-testid={`${testIdPrefix}-tag-chips-${entityId}`}
          >
            {chips}
          </Flex>
        ) : null}
      </div>
    );
  }

  return (
    <Flex
      align="center"
      gap="1"
      wrap={inline ? "nowrap" : "wrap"}
      data-testid={dataTestId ?? `${testIdPrefix}-label-editor`}
      aria-label="Tags"
      className={inline ? "min-w-0 max-w-full overflow-x-auto" : undefined}
    >
      {chips}
      {addControl}
    </Flex>
  );
}

/** File-row / review-header tag editor that persists via PATCH. */
export function FileTagEditor({
  projectId,
  fileId,
  tags,
  testIdPrefix,
  leading,
  chipsBelow = false,
  inline = false,
}: {
  projectId: string;
  fileId: string;
  tags?: string[];
  testIdPrefix: string;
  leading?: ReactNode;
  chipsBelow?: boolean;
  inline?: boolean;
}) {
  const updateFileLabels = useUpdateFileLabels();
  return (
    <OrgLabelEditor
      testIdPrefix={testIdPrefix}
      entityId={fileId}
      data-testid={`${testIdPrefix}-tag-editor-${fileId}`}
      value={tags ?? []}
      inline={inline}
      chipsBelow={chipsBelow}
      leading={leading}
      onChange={(next) =>
        updateFileLabels.mutate({
          projectId,
          fileId,
          payload: { tags: next },
        })
      }
    />
  );
}
